import React from 'react';
import MetricCard from '../../common/components/atom/MetricCard/MetricCard';
import { VoltageIcon, CurrentIcon, FrequencyIcon, PowerFactorIcon } from '../../common/components/atom/MetricCard/icons';
import { formatMetricValue, formatMetricDate } from '../../common/utils/metricFormat';
import { useVoltage, useCurrent, useFrequency, useCredit, useActivePower, usePowerFactor, useNbTails, useNbCuts, useLastConsumptionIndex, useTotalCreditPurchased, useRelayStatus } from '../../features/Jouly/hooks';
import { StatusCardPresenter } from '../../common/components/atom/StatusCard';
import { useParams } from 'react-router-dom';
import styles from './LastValuesMetrics.module.scss';

export default function LastValuesMetrics() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const { lastVoltageQuery } = useVoltage();
  const { currentQuery } = useCurrent();
  const { frequencyQuery } = useFrequency();
  const { lastCreditQuery } = useCredit();
  const { activePowerQuery } = useActivePower();
  const { lastPowerFactorQuery } = usePowerFactor();
  const { nbTailsQuery } = useNbTails(deviceId);
  const { nbCutsQuery } = useNbCuts(deviceId);
  const { lastConsumptionIndexQuery } = useLastConsumptionIndex(deviceId);
  const { totalKwhPurchased, isLoading: isTotalCreditLoading } = useTotalCreditPurchased(deviceId);
  const { relayStatusQuery } = useRelayStatus(deviceId);





  // Fonction pour déterminer le status selon la valeur
  const getStatus = (type: string, value: number | undefined): 'ok' | 'warning' | 'danger' => {
    if (value === undefined || value === null) return 'danger';
    
    switch (type) {
      case 'voltage':
        // Tension normale: 220V ± 10% (198V - 242V)
        if (value >= 198 && value <= 242) return 'ok';
        if (value >= 180 && value < 198) return 'warning';
        if (value > 242 && value <= 260) return 'warning';
        return 'danger';
        
      case 'current':
        // Courant normal: 0-20A
        if (value >= 0 && value <= 20) return 'ok';
        if (value > 20 && value <= 30) return 'warning';
        return 'danger';
        
      case 'frequency':
        // Fréquence normale: 50Hz ± 2% (49Hz - 51Hz)
        if (value >= 49 && value <= 51) return 'ok';
        if (value >= 47 && value < 49) return 'warning';
        if (value > 51 && value <= 53) return 'warning';
        return 'danger';
        
      case 'credit':
        // Crédit: > 10kWh = ok, 5-10kWh = warning, < 5kWh = danger
        if (value >= 10) return 'ok';
        if (value >= 5 && value < 10) return 'warning';
        return 'danger';
        
      case 'activePower':
        // Puissance active: toujours ok (vert)
        return 'ok';
        
      case 'powerFactor':
        // Facteur de puissance: 0.9-1.0 = ok, 0.8-0.9 = warning, < 0.8 = danger
        if (value >= 0.9 && value <= 1.0) return 'ok';
        if (value >= 0.8 && value < 0.9) return 'warning';
        return 'danger';
        
      case 'consumptionIndex':
        // Index de consommation: toujours ok (pas de seuil défini)
        return 'ok';
        
      default:
        return 'ok';
    }
  };

  const handleStatusChange = (isOnline: boolean) => {
    console.log(`Statut du compteur ${deviceId}: ${isOnline ? 'En ligne' : 'Hors ligne'}`);
  };

  const formatValue = formatMetricValue;
  const formatDate = formatMetricDate;

  return (
    <div className={styles.lastValuesContainer}>
      <div className={styles.metricsLayout}>
        <div className={styles.statusSection}>
          <StatusCardPresenter
            deviceId={deviceId}
            onStatusChange={handleStatusChange}
            showLastSeen={true}
          />
        </div>
        
        <div className={styles.metricsGrid}>
          <MetricCard
            title="Tension"
            value={lastVoltageQuery.isLoading ? '...' : formatValue(lastVoltageQuery.data?.[0]?.Value, 1)}
            unit="V"
            icon={<VoltageIcon />}
            status={getStatus('voltage', lastVoltageQuery.data?.[0]?.Value)}
            showPercentage={false}
            showIcon={false}
            tooltip={lastVoltageQuery.isLoading ? undefined : `Capturé le: ${formatDate(lastVoltageQuery.data?.[0]?.Time)}`}
          />
          
          <MetricCard
            title="Courant"
            value={currentQuery.isLoading ? '...' : formatValue(currentQuery.data?.[0]?.Value, 2)}
            unit="A"
            icon={<CurrentIcon />}
            status={getStatus('current', currentQuery.data?.[0]?.Value)}
            showPercentage={false}
            showIcon={false}
            tooltip={currentQuery.isLoading ? undefined : `Capturé le: ${formatDate(currentQuery.data?.[0]?.Time)}`}
          />
          
          <MetricCard
            title="Fréquence"
            value={frequencyQuery.isLoading ? '...' : formatValue(frequencyQuery.data?.[0]?.Value, 2)}
            unit="Hz"
            icon={<FrequencyIcon />}
            status={getStatus('frequency', frequencyQuery.data?.[0]?.Value)}
            showPercentage={false}
            showIcon={false}
            tooltip={frequencyQuery.isLoading ? undefined : `Capturé le: ${formatDate(frequencyQuery.data?.[0]?.Time)}`}
          />
          
          <MetricCard
            title="Crédit restant"
            value={lastCreditQuery.isLoading ? '...' : formatValue(lastCreditQuery.data?.[0]?.Value, 2)}
            unit="kWh"
            icon={<PowerFactorIcon />}
            status={getStatus('credit', lastCreditQuery.data?.[0]?.Value)}
            showPercentage={false}
            showIcon={false}
            tooltip={lastCreditQuery.isLoading ? undefined : `Capturé le: ${formatDate(lastCreditQuery.data?.[0]?.Time)}`}
          />
          
          <MetricCard
            title="Puissance Active"
            value={activePowerQuery.isLoading ? '...' : formatValue(activePowerQuery.data?.[0]?.Value, 2)}
            unit="W"
            icon={<PowerFactorIcon />}
            status={getStatus('activePower', activePowerQuery.data?.[0]?.Value)}
            showPercentage={false}
            showIcon={false}
            tooltip={activePowerQuery.isLoading ? undefined : `Capturé le: ${formatDate(activePowerQuery.data?.[0]?.Time)}`}
          />
          
          <MetricCard
            title="Facteur de Puissance"
            value={lastPowerFactorQuery.isLoading ? '...' : formatValue(lastPowerFactorQuery.data?.[0]?.Value, 2)}
            unit=""
            icon={<PowerFactorIcon />}
            status={getStatus('powerFactor', lastPowerFactorQuery.data?.[0]?.Value)}
            showPercentage={false}
            showIcon={false}
            tooltip={lastPowerFactorQuery.isLoading ? undefined : `Capturé le: ${formatDate(lastPowerFactorQuery.data?.[0]?.Time)}`}
          />
          
          <MetricCard
            title="Nombre d'Ouvertures"
            value={nbTailsQuery.isLoading ? '...' : formatValue(nbTailsQuery.data?.[0]?.Value, 0)}
            unit=""
            icon={<PowerFactorIcon />}
            status="ok"
            showPercentage={false}
            showIcon={false}
            tooltip={nbTailsQuery.isLoading ? undefined : `Capturé le: ${formatDate(nbTailsQuery.data?.[0]?.Time)}`}
          />
          
          <MetricCard
            title="Nombre de Coupures"
            value={nbCutsQuery.isLoading ? '...' : formatValue(nbCutsQuery.data?.[0]?.Value, 0)}
            unit=""
            icon={<PowerFactorIcon />}
            status="warning"
            showPercentage={false}
            showIcon={false}
            tooltip={nbCutsQuery.isLoading ? undefined : `Capturé le: ${formatDate(nbCutsQuery.data?.[0]?.Time)}`}
          />
          
          <MetricCard
            title="Index de Consommation"
            value={lastConsumptionIndexQuery.isLoading ? '...' : formatValue(lastConsumptionIndexQuery.data?.[0]?.Value, 2)}
            unit="kWh"
            icon={<PowerFactorIcon />}
            status={getStatus('consumptionIndex', lastConsumptionIndexQuery.data?.[0]?.Value)}
            showPercentage={false}
            showIcon={false}
            tooltip={lastConsumptionIndexQuery.isLoading ? undefined : `Capturé le: ${formatDate(lastConsumptionIndexQuery.data?.[0]?.Time)}`}
          />

          <MetricCard
            title="Crédit total acheté"
            value={isTotalCreditLoading ? '...' : formatValue(totalKwhPurchased, 2)}
            unit="kWh"
            icon={<PowerFactorIcon />}
            status="ok"
            showPercentage={false}
            showIcon={false}
            tooltip="Total des kWh achetés via les paiements"
          />

          <MetricCard
            title="Crédit total consommé"
            value={lastConsumptionIndexQuery.isLoading ? '...' : formatValue(lastConsumptionIndexQuery.data?.[0]?.Value, 2)}
            unit="kWh"
            icon={<PowerFactorIcon />}
            status="ok"
            showPercentage={false}
            showIcon={false}
            tooltip="Total des kWh consommés (index de consommation)"
          />

          <MetricCard
            title="Statut relais"
            value={relayStatusQuery.isLoading ? '...' : relayStatusQuery.data?.relay_on ? 'ON' : 'OFF'}
            unit=""
            icon={<PowerFactorIcon />}
            status={relayStatusQuery.isLoading ? 'ok' : relayStatusQuery.data?.relay_on ? 'ok' : 'danger'}
            showPercentage={false}
            showIcon={false}
            tooltip={relayStatusQuery.isLoading ? undefined : `Relais ${relayStatusQuery.data?.relay_on ? 'fermé (ON)' : 'ouvert (OFF)'}`}
          />
        </div>
      </div>
    </div>
  );
}
